/* eslint-disable spellcheck/spell-checker */
/* eslint-disable @typescript-eslint/naming-convention */
import { Json } from '../types.model';
import {
    AppPhase,
    DealerAssistTableErrorCode,
    DealingSubPhase,
    FlagStateType,
    GameType,
    RouletteSubPhase,
    SupervisorAction,
    SupervisorSubPhase,
    TableMode,
    TableType,
    WorkerAccessLevel
} from './app-enums.model';
import { CardValue } from './cards.enum';
import { ChatMessage } from './chat-message.model';
import { AddHandValues, CardPosition, HandValueKey, Outcome } from './games.model';

export type FlagsState = {
    -readonly [Property in keyof typeof FlagStateType as Uncapitalize<Property>]?: boolean;
};

export const hasOnlyNextCardPosition = (value: TableState): value is OnlyNextCardPosition => {
    return 'nextCardPosition' in value && Object.keys(value).length === 1;
};

export type TableState = OnlyNextCardPosition | TableStateDealing;

interface OnlyNextCardPosition {
    nextCardPosition: CardPosition;
}

interface TableStateBase {
    cardPosition: CardPosition;
    handValue: Partial<Record<HandValueKey, number>>;
    nextCardPosition?: CardPosition;
    cards: Array<{ card: CardValue; index: CardPosition }>;
}

export interface TableStateDealing extends TableStateBase {
    faceDownCards?: Array<[CardPosition, CardValue]>;
}

// Normalized
export interface InitialTableState extends TableStateBase {
    faceDownCards?: Array<{ card: CardValue; index: CardPosition }>;
}

export type TableUpdate = AddHandValues<{
    card?: CardValue;
    cardPosition?: CardPosition;
    nextCardPosition?: CardPosition;
    outcome?: Outcome;
    outcomes?: Outcome[];
}>;

export interface Worker {
    id: string;
    name: string;
    accessLevel: WorkerAccessLevel;
}

export interface SupervisorState {
    subPhase?: SupervisorSubPhase;
    selectedGame?: Game;
    selectedAction?: SupervisorAction;
    changedGameResult?: Json;
}

export interface Game {
    id: number;
    gameType: GameType;
    displayName: string;
    isVirtual: boolean;
}

export interface GameResultChange {
    cards: Partial<Record<CardPosition, CardValue | undefined>>;
    handValue: Partial<Record<HandValueKey, number>>;
    nextCardPosition?: CardPosition;
    outcome?: Outcome;
    outcomes?: Outcome[];
}

export interface HistoryGameBase {
    croupier: string;
    gameType: GameType;
    id: number;
    openTime: number;
    status: string;
    table: string | null;
    updateTime: number;
}

export interface HistoryGameClosed extends HistoryGameBase {
    status: 'Closed';
    state: {
        cards: Array<{ card: CardValue; index: CardPosition }>;
        extraData: unknown;
        gameNumber: number;
        gameType?: GameType;
        handValue: number;
        handValues: Record<string, number>;
        winner: Outcome[];
    };
}

export interface HistoryGameVoid extends HistoryGameBase {
    status: 'Void';
    state: {
        roundState: {
            phase: string;
            roundId: number;
        };
        tableState?: {
            cardPosition: number;
            cards: Array<{ card: CardValue; index: CardPosition }>;
            handValue: number | null;
            nextCardPosition: number;
        };
    };
}

export interface RouletteSpeed {
    ballSpeed: {
        current: number;
        max: number;
        min: number;
    };
    wheelSpeed: {
        current: number;
        max: number;
        min: number;
    };
}

export type HistoryGame = HistoryGameClosed | HistoryGameVoid;

export interface AppState {
    configuration: {
        appVersion: string;
        tableId: string;
        bettingWindowInSeconds: number;
        hurryUpTimeInSeconds?: number;
        betsOpenAfterBallThrowInSeconds?: number;
        decisionTimeInSeconds?: number;
        videoStreamId?: string;
        game?: Game;
        tableMode: TableMode;
        tableType?: TableType;
        supervisorActions: SupervisorAction[];
    };

    playerCount?: number;

    worker?: Worker;

    supervisorState?: SupervisorState;

    roundState: {
        phase: AppPhase;
        subPhase?: DealingSubPhase | RouletteSubPhase;
        roundId?: number;
        remainingBettingTimeInSeconds?: number;
        ballShootInTimeLeft?: number;
        errorCode?: DealerAssistTableErrorCode;
    };

    tableState?: TableState; // Initial table data
    initialTableState?: InitialTableState; // Normalized tableState
    tableUpdate?: TableUpdate;

    gameResultChange?: GameResultChange;

    shoeChangeState?: {
        cutCardDrawn?: boolean;
        card?: CardValue;
        burnedCount?: number;
        totalToBurnCount?: number;
    };

    flagsState: FlagsState;

    chatMessages?: ChatMessage[]; // Newest messages first

    games?: {
        availableGames: Game[];
        unfinishedGame?: Game;
    };

    history?: HistoryGame[];
}
